import { useTranslation } from 'react-i18next';

import { normalizeLanguage } from '../utils';

export type I18nFieldValue = string | Record<string, string> | undefined | null;

/**
 * 从多语言 dict 字段按当前 i18n 语言取值，兼容历史 string 数据。
 *
 * 回退顺序：
 *   1. 当前语言 key
 *   2. fallbackLang（默认 ja，面向日本市场）
 *   3. 任意非空值
 *   4. 空字符串
 *
 * @param field 多语言 dict {lang: text}，或历史 string，或空值
 * @param fallbackLang 回退语言，默认 ja
 */
export const useI18nField = (field: I18nFieldValue, fallbackLang = 'ja'): string => {
  const { i18n } = useTranslation();
  if (field == null) return '';
  if (typeof field === 'string') return field;
  const lang = normalizeLanguage?.(i18n.language) ?? i18n.language;
  return (
    field[lang] || field[i18n.language] || field[fallbackLang] || Object.values(field).find((v) => v && v.trim()) || ''
  );
};

/**
 * 非 hook 版本，用于非 React 上下文（如工具函数、store 选择器）。
 */
export const getI18nFieldValue = (field: I18nFieldValue, lang: string, fallbackLang = 'ja'): string => {
  if (field == null) return '';
  if (typeof field === 'string') return field;
  return field[lang] || field[fallbackLang] || Object.values(field).find((v) => v && v.trim()) || '';
};
